import { useState, type ReactNode } from 'react';
import { useEscapeLayer } from './Modal';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Confirmation modal shared by both dashboards. Uses semantic Tailwind tokens
 * (bg-card, text-foreground, bg-destructive, overlay-backdrop) driven by each
 * app's CSS variables, so it renders in each app's theme.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [loading, setLoading] = useState(false);
  // Escape cancels THIS dialog — not the modal it was opened over.
  useEscapeLayer(open, () => { if (!loading) onCancel(); });

  if (!open) return null;

  const btnColor = variant === 'danger' ? 'bg-destructive hover:bg-red-600' : 'bg-brand hover:bg-brand-dark';

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overlay-backdrop">
      <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-modal border border-border">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        <p className="mt-2 text-[13px] text-muted-foreground">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`rounded-lg px-4 py-2 text-[13px] font-medium text-white ${btnColor} transition-colors disabled:opacity-50`}
          >
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
